import { useEffect, useMemo, useState } from "react";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  KpiCard,
  Typography,
} from "../../../../components";
import {
  BancosCaixasService,
  CaixaService,
  SessionService,
} from "../../../../services";
import { cacheApi, formatPrice, responseErros } from "../../../../utils";
import { DashboardChart } from "../../../main/components";
import { filtrosUrl } from "../../../../helpers";
import { useCacheContext } from "../../../../contexts/cache.context";
import {
  dataDiaAnterior,
  filtrosIniciais,
  modoVisualizacao,
} from "./fluxo-de-caixa";
import { FluxoCaixaFiltros } from "./components";

const caixaService = new CaixaService();
const bancosCaixasService = new BancosCaixasService();
const sessionService = new SessionService();

const PERIODOS = ["Diário", "Semanal", "Mensal"];
const PERIODO_PARAM = { Diário: "DIA", Semanal: "SEMANA", Mensal: "MES" };

const SERIES_DEFS = {
  realizado: [
    {
      key: "entradas",
      name: "Entradas",
      color: "#22c55e",
      dataKey: "entradas",
    },
    { key: "saidas", name: "Saídas", color: "#ef4444", dataKey: "saidas" },
    {
      key: "saldo",
      name: "Saldo Acumulado",
      color: "#3b82f6",
      dataKey: "saldoAcumulado",
      isLine: true,
    },
  ],
  previsto: [
    {
      key: "entradasPrev",
      name: "Entradas Previstas",
      color: "#86efac",
      dataKey: "entradasPrevistas",
      isDashed: true,
    },
    {
      key: "saidasPrev",
      name: "Saídas Previstas",
      color: "#fca5a5",
      dataKey: "saidasPrevistas",
      isDashed: true,
    },
    {
      key: "saldoPrev",
      name: "Saldo Acumulado Previsto",
      color: "#93c5fd",
      dataKey: "saldoAcumuladoPrevisto",
      isLine: true,
      isDashed: true,
    },
  ],
  previsto_x_realizado: [
    {
      key: "entradas",
      name: "Entradas",
      color: "#22c55e",
      dataKey: "entradas",
    },
    {
      key: "entradasPrev",
      name: "Entradas Previstas",
      color: "#86efac",
      dataKey: "entradasPrevistas",
      isDashed: true,
    },
    { key: "saidas", name: "Saídas", color: "#ef4444", dataKey: "saidas" },
    {
      key: "saidasPrev",
      name: "Saídas Previstas",
      color: "#fca5a5",
      dataKey: "saidasPrevistas",
      isDashed: true,
    },
    {
      key: "saldo",
      name: "Saldo Acumulado",
      color: "#3b82f6",
      dataKey: "saldoAcumulado",
      isLine: true,
    },
  ],
};

const ALL_SERIES_KEYS = [
  ...new Set(
    Object.values(SERIES_DEFS).flatMap((defs) => defs.map((d) => d.key)),
  ),
];

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

function agruparPorPeriodo(dados, agrupamento) {
  if (agrupamento === "DIA") return dados;
  const mapa = new Map();
  for (const d of dados) {
    const [dd, mm, yyyy] = d.label.split("/");
    const data = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    let chave;
    let label;
    let labelCurto;
    if (agrupamento === "MES") {
      chave = `${yyyy}-${mm}`;
      label = MESES[Number(mm) - 1];
      labelCurto = label;
    } else {
      const inicio = new Date(data);
      inicio.setDate(data.getDate() - data.getDay());
      const fim = new Date(inicio);
      fim.setDate(inicio.getDate() + 6);
      const di = String(inicio.getDate()).padStart(2, "0");
      const mi = String(inicio.getMonth() + 1).padStart(2, "0");
      const df = String(fim.getDate()).padStart(2, "0");
      const mf = String(fim.getMonth() + 1).padStart(2, "0");
      chave = `${inicio.getFullYear()}-${inicio.getMonth()}-${inicio.getDate()}`;
      label = `Sem ${di}/${mi} - ${df}/${mf}`;
      labelCurto = `${di}/${mi}`;
    }
    let grupo = mapa.get(chave);
    if (!grupo) {
      grupo = {
        label,
        labelCurto,
        _data: new Date(data),
        entradas: 0,
        saidas: 0,
        entradasPrevistas: 0,
        saidasPrevistas: 0,
        saldoAcumulado: 0,
        saldoAcumuladoPrevisto: 0,
      };
      mapa.set(chave, grupo);
    }
    grupo.entradas += d.entradas ?? 0;
    grupo.saidas += d.saidas ?? 0;
    grupo.entradasPrevistas += d.entradasPrevistas ?? 0;
    grupo.saidasPrevistas += d.saidasPrevistas ?? 0;
    if (data >= grupo._data) {
      grupo._data = data;
      grupo.saldoAcumulado = d.saldoAcumulado ?? 0;
      grupo.saldoAcumuladoPrevisto = d.saldoAcumuladoPrevisto ?? 0;
    }
  }
  return Array.from(mapa.values()).sort((a, b) => a._data - b._data);
}

const CARDS_REALIZADO = [
  {
    key: "totalEntradas",
    label: "Total de Entradas",
    color: "#1976d2",
    icon: "ph-arrow-circle-down",
  },
  {
    key: "totalSaidas",
    label: "Total de Saídas",
    color: "#d32f2f",
    icon: "ph-arrow-circle-up",
  },
  {
    key: "saldoPeriodo",
    label: "Saldo do Período",
    color: "#388e3c",
    icon: "ph-currency-dollar",
  },
  {
    key: "saldoAcumulado",
    label: "Saldo Acumulado",
    color: "#00796b",
    icon: "ph-bank",
  },
];

const MODO_CARDS = {
  realizado: CARDS_REALIZADO,
  previsto: [
    {
      key: "totalEntradasPrevistas",
      label: "Entradas Previstas",
      color: "#0288d1",
      icon: "ph-arrow-circle-down",
    },
    {
      key: "totalSaidasPrevistas",
      label: "Saídas Previstas",
      color: "#f57c00",
      icon: "ph-arrow-circle-up",
    },
    {
      key: "saldoPrevisto",
      label: "Saldo Previsto",
      color: "#7b1fa2",
      icon: "ph-currency-dollar",
    },
    {
      key: "saldoAcumuladoPrevisto",
      label: "Saldo Acumulado Previsto",
      color: "#00796b",
      icon: "ph-bank",
    },
  ],
  previsto_x_realizado: CARDS_REALIZADO,
};

const TH_SX = {
  fontWeight: 700,
  color: "#475569",
  borderBottom: "1px solid #e2e8f0",
  fontSize: 13,
  textAlign: "center",
  textTransform: "uppercase",
};

const TD_SX = {
  fontSize: 14,
  color: "#1a1a1a",
  borderBottom: "1px solid #e8ecf0",
  py: 1.5,
};

const COR_POSITIVO = "#16a34a";
const COR_NEGATIVO = "#dc2626";

const corEntrada = COR_POSITIVO;
const corSaida = COR_NEGATIVO;
const corSaldo = (valor) => (valor < 0 ? COR_NEGATIVO : COR_POSITIVO);

function LegendItem({ def, visible, onToggle }) {
  return (
    <Box
      onClick={() => onToggle(def.key)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        cursor: "pointer",
        userSelect: "none",
        opacity: visible ? 1 : 0.38,
        "&:hover": { opacity: visible ? 0.6 : 0.55 },
        transition: "opacity 0.15s",
      }}
    >
      <Box
        sx={{
          width: 13,
          height: 13,
          borderRadius: "3px",
          border: `2px solid ${def.color}`,
          bgcolor: visible ? def.color : "transparent",
          transition: "background-color 0.15s",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {visible && (
          <svg width="8" height="8" viewBox="0 0 8 8">
            <path
              d="M1 4L3 6L7 2"
              stroke="white"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </Box>
      <Box
        sx={{
          width: 20,
          height: 2,
          flexShrink: 0,
          ...(def.isDashed
            ? {
                backgroundImage: `repeating-linear-gradient(to right, ${def.color} 0px, ${def.color} 5px, transparent 5px, transparent 8px)`,
              }
            : { bgcolor: def.color }),
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: "#475569",
          fontSize: 12,
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        {def.name}
      </Typography>
    </Box>
  );
}

function SaldoAnteriorRow({ totalColunas, valor, dataLabel, porCaixa }) {
  const cellSx = { ...TD_SX, backgroundColor: "#f8fafc" };
  return (
    <TableRow>
      <TableCell
        sx={{
          ...cellSx,
          fontWeight: 700,
          color: "#475569",
          position: "sticky",
          left: 0,
          zIndex: 1,
          ...(totalColunas === 8 && { borderRight: "2px solid #cbd5e1" }),
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75}>
          <i
            className={`ph-bold ${
              porCaixa ? "ph-wallet" : "ph-clock-counter-clockwise"
            }`}
            style={{ fontSize: 15, color: "#94a3b8" }}
          />
          {porCaixa ? "Saldo dos caixas" : "Saldo anterior"} — ({dataLabel})
        </Box>
      </TableCell>
      <TableCell colSpan={totalColunas - 2} sx={cellSx} />
      <TableCell
        align="right"
        sx={{
          ...cellSx,
          fontWeight: 700,
          borderRight: "none",
          color: corSaldo(valor),
        }}
      >
        {formatPrice(valor ?? 0, false)}
      </TableCell>
    </TableRow>
  );
}

const FluxoDeCaixa = () => {
  const { cacheDto, setCacheDto } = useCacheContext();
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();
  const acessosEmpresas = usuario?.acessosEmpresas || [];
  const filtrosPadrao = {
    ...filtrosIniciais,
    empresas: empresa ? [empresa] : [],
  };

  const [loading, setLoading] = useState(true);
  const [totais, setTotais] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [visibleSeries, setVisibleSeries] = useState(
    () => new Set(ALL_SERIES_KEYS),
  );
  const [filtros, setFiltros] = useState(filtrosPadrao);
  const [draft, setDraft] = useState(filtrosPadrao);
  const [listas, setListas] = useState({ contas: [] });
  const [openFiltros, setOpenFiltros] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(31);

  const modo = modoVisualizacao(filtros);
  const bancoCaixaIds = (filtros.bancosCaixas ?? []).map((conta) => conta.id);
  const bancoCaixaIdsKey = bancoCaixaIds.join(",");
  const empresasIds = (filtros.empresas ?? []).map((item) => item.id);
  const empresasIdsKey = empresasIds.join(",");

  const filtrosAtivos =
    (filtros.previsto && filtros.realizado ? 0 : 1) +
    (filtros.considerarSaldoCaixa ? 1 : 0) +
    (acessosEmpresas.length > 0 ? empresasIds.length : 0) +
    bancoCaixaIds.length;

  useEffect(() => {
    buscarBancosCaixas();
  }, []);

  useEffect(() => {
    buscarGraficoETotais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filtros.dataInicio,
    filtros.dataFim,
    filtros.considerarSaldoCaixa,
    bancoCaixaIdsKey,
    empresasIdsKey,
  ]);

  const buscarBancosCaixas = async () => {
    const result = await bancosCaixasService.getAll({ nonPaginated: true });
    if (!result.isAxiosError) {
      setListas((prev) => ({
        ...prev,
        contas: result.data?.rows || result.data || [],
      }));
    } else {
      responseErros(result);
    }
  };

  const buscarGraficoETotais = async () => {
    setLoading(true);
    try {
      const label = filtrosUrl.getUrl();
      const cacheBody = {
        dto: { cacheDto, setCacheDto },
        timeMinutes: 1000,
        apiFunction: caixaService.getRelatorioFluxoCaixa({
          dataInicio: filtros.dataInicio,
          dataFim: filtros.dataFim,
          agrupamento: "DIA",
          considerarSaldoCaixa: filtros.considerarSaldoCaixa,
          bancoCaixaIds: bancoCaixaIds.length ? bancoCaixaIds : undefined,
          empresasIds: empresasIds.length ? empresasIds : undefined,
        }),
      };
      const result = await cacheApi(
        `${label}-${filtros.dataInicio}-${filtros.dataFim}-${filtros.considerarSaldoCaixa}-${bancoCaixaIdsKey}-${empresasIdsKey}-fluxo`,
        cacheBody,
      );
      if (!result.isAxiosError) {
        setTotais(result.data.resumo);
        setRawData(result.data.chartData || []);
      } else {
        responseErros(result);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const abrirFiltros = () => {
    setDraft(filtros);
    setOpenFiltros(true);
  };

  const aplicarFiltros = (novosFiltros) => {
    setPage(0);
    setFiltros(novosFiltros);
  };

  const onChangeAgrupamento = (agrupamento) => {
    setPage(0);
    setFiltros((prev) => ({ ...prev, agrupamento }));
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleSeries = (key) => {
    setVisibleSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const currentSeriesDefs = SERIES_DEFS[modo] || SERIES_DEFS.realizado;

  const dadosAgrupados = useMemo(
    () => agruparPorPeriodo(rawData, filtros.agrupamento),
    [rawData, filtros.agrupamento],
  );

  const gridRows = useMemo(
    () =>
      dadosAgrupados.map((d, i) => {
        const entradas = d.entradas ?? 0;
        const saidas = d.saidas ?? 0;
        const entradasPrevistas = d.entradasPrevistas ?? 0;
        const saidasPrevistas = d.saidasPrevistas ?? 0;
        return {
          id: d.label ?? i,
          label: d.label,
          entradas,
          saidas,
          saldoDia: entradas - saidas,
          saldoAcumulado: d.saldoAcumulado ?? 0,
          entradasPrevistas,
          saidasPrevistas,
          saldoPrevisto: entradasPrevistas - saidasPrevistas,
          saldoAcumuladoPrevisto: d.saldoAcumuladoPrevisto ?? 0,
        };
      }),
    [dadosAgrupados],
  );

  const totalCount = gridRows.length;

  const paginatedRows = useMemo(
    () => gridRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [gridRows, page, rowsPerPage],
  );

  const activeChartData = useMemo(() => {
    const defs = SERIES_DEFS[modo] || SERIES_DEFS.realizado;
    return {
      categories: dadosAgrupados.map((d) =>
        filtros.agrupamento === "DIA"
          ? d.label.slice(0, 5)
          : d.labelCurto ?? d.label,
      ),
      series: defs.map((def) => ({
        name: def.name,
        type: def.isLine ? "line" : "area",
        color: def.color,
        dashStyle: def.isDashed ? "Dash" : "Solid",
        fillOpacity: def.isDashed ? 0.07 : 0.18,
        lineWidth: def.isLine ? 2.5 : 2,
        marker: { enabled: false },
        visible: visibleSeries.has(def.key),
        showInLegend: false,
        data: dadosAgrupados.map((d) => d[def.dataKey] ?? 0),
      })),
    };
  }, [modo, dadosAgrupados, visibleSeries, filtros.agrupamento]);

  const chartOptions = useMemo(
    () => ({
      chart: { type: "area", style: { fontFamily: "inherit" } },
      plotOptions: {
        area: { marker: { enabled: false }, lineWidth: 2 },
        line: { marker: { enabled: false }, lineWidth: 2.5 },
      },
      legend: { enabled: false },
      yAxis: {
        labels: {
          formatter: function () {
            const abs = Math.abs(this.value);
            if (abs >= 1000) return `${(this.value / 1000).toFixed(0)}k`;
            return `${this.value}`;
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          let s = `<b>${this.x}</b><br/>`;
          this.points?.forEach((point) => {
            s += `<span style="color:${point.color}">●</span> ${
              point.series.name
            }: <b>${formatPrice(point.y, false)}</b><br/>`;
          });
          return s;
        },
      },
    }),
    [],
  );

  const activeCards = MODO_CARDS[modo] || MODO_CARDS.realizado;
  const totalColunas = modo === "previsto_x_realizado" ? 8 : 5;
  const labelDiaAnterior = dataDiaAnterior(filtros.dataInicio);

  return (
    <Card className="minimal-layout-card">
      <CardContent className="minimal-layout-card-content rolagem">
        <Box className="w-100 ms-auto" sx={{ pb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            gap={2}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Fluxo de Caixa
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acompanhe entradas, saídas e saldo do período
              </Typography>
            </Box>

            <Badge
              badgeContent={filtrosAtivos}
              color="primary"
              overlap="rectangular"
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={abrirFiltros}
                startIcon={<i className="ph-fill ph-sliders-horizontal" />}
                sx={{ whiteSpace: "nowrap" }}
              >
                Filtros
              </Button>
            </Badge>
          </Box>

          <Box mb={3}>
            <FluxoCaixaFiltros
              filtros={filtros}
              filtrosPadrao={filtrosPadrao}
              onAplicar={aplicarFiltros}
              listas={listas}
              acessosEmpresas={acessosEmpresas}
              draft={draft}
              setDraft={setDraft}
              open={openFiltros}
              setOpen={setOpenFiltros}
            />
          </Box>

          <Grid container spacing={2} mb={3}>
            {activeCards.map((card) => (
              <Grid item xs={6} md={3} key={card.key}>
                <KpiCard
                  title={card.label}
                  value={formatPrice((totais || {})[card.key] ?? 0, false)}
                  color={card.color}
                  icon={card.icon}
                  loading={loading}
                />
              </Grid>
            ))}
          </Grid>

          <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0", mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
                flexWrap="wrap"
                gap={1.5}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Evolução Financeira
                </Typography>
                <Box display="flex" gap={1}>
                  {PERIODOS.map((p) => (
                    <Button
                      key={p}
                      onClick={() => onChangeAgrupamento(PERIODO_PARAM[p])}
                      variant={
                        filtros.agrupamento === PERIODO_PARAM[p]
                          ? "contained"
                          : "outlined"
                      }
                      disableElevation
                      sx={{
                        textTransform: "none",
                        fontWeight:
                          filtros.agrupamento === PERIODO_PARAM[p] ? 700 : 400,
                      }}
                    >
                      {p}
                    </Button>
                  ))}
                </Box>
              </Box>

              {loading ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={300}
                  sx={{ borderRadius: 2 }}
                />
              ) : (
                <DashboardChart
                  loading={loading}
                  data={
                    activeChartData.series.length
                      ? activeChartData
                      : { categories: [], series: [] }
                  }
                  heightChart={300}
                  options={chartOptions}
                />
              )}

              <Box
                display="flex"
                flexWrap="wrap"
                gap={2}
                mt={2}
                justifyContent="center"
              >
                {currentSeriesDefs.map((def) => (
                  <LegendItem
                    key={def.key}
                    def={def}
                    visible={visibleSeries.has(def.key)}
                    onToggle={toggleSeries}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3, border: "1px solid #f0f0f0" }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Detalhamento do Período
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Valores{" "}
                {filtros.agrupamento === "DIA"
                  ? "diários"
                  : filtros.agrupamento === "SEMANA"
                  ? "semanais"
                  : "mensais"}{" "}
                — entradas, saídas e saldo
              </Typography>

              <TableContainer>
                <Table size="medium">
                  {modo === "previsto_x_realizado" ? (
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell
                          rowSpan={2}
                          sx={{
                            ...TH_SX,
                            textAlign: "left",
                            verticalAlign: "bottom",
                            borderRight: "2px solid #cbd5e1",
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          Data
                        </TableCell>
                        <TableCell
                          colSpan={2}
                          sx={{ ...TH_SX, borderRight: "1px solid #e2e8f0" }}
                        >
                          Entradas
                        </TableCell>
                        <TableCell
                          colSpan={2}
                          sx={{ ...TH_SX, borderRight: "1px solid #e2e8f0" }}
                        >
                          Saídas
                        </TableCell>
                        <TableCell
                          colSpan={3}
                          sx={{ ...TH_SX, borderRight: "none" }}
                        >
                          Saldos
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                        {[
                          "Previsto",
                          "Realizado",
                          "Previsto",
                          "Realizado",
                          "Previsto",
                          "Realizado",
                          "Acumulado",
                        ].map((label, i) => (
                          <TableCell
                            key={i}
                            sx={{
                              ...TH_SX,
                              fontWeight: 700,
                              color:
                                label === "Previsto" ? "#94a3b8" : "#475569",
                              fontSize: 13,
                              ...(i === 1 || i === 3
                                ? { borderRight: "1px solid #e2e8f0" }
                                : {}),
                              ...(i === 6 ? { borderRight: "none" } : {}),
                            }}
                          >
                            {label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                  ) : (
                    <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell
                          sx={{
                            ...TH_SX,
                            textAlign: "left",
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          Data
                        </TableCell>
                        <TableCell sx={TH_SX}>
                          {modo === "previsto"
                            ? "Entradas Previstas"
                            : "Entradas"}
                        </TableCell>
                        <TableCell sx={TH_SX}>
                          {modo === "previsto" ? "Saídas Previstas" : "Saídas"}
                        </TableCell>
                        <TableCell sx={TH_SX}>
                          {modo === "previsto"
                            ? "Saldo Previsto"
                            : "Saldo do Dia"}
                        </TableCell>
                        <TableCell sx={{ ...TH_SX, borderRight: "none" }}>
                          Saldo Acumulado
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  )}

                  <TableBody>
                    {loading ? (
                      Array.from(new Array(rowsPerPage)).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from(new Array(totalColunas)).map((__, ci) => (
                            <TableCell key={ci}>
                              <Skeleton animation="wave" height={24} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <>
                        {page === 0 && totais && (
                          <SaldoAnteriorRow
                            totalColunas={totalColunas}
                            valor={totais.saldoAnterior}
                            dataLabel={labelDiaAnterior}
                            porCaixa={filtros.considerarSaldoCaixa}
                          />
                        )}
                        {paginatedRows.length > 0 ? (
                          paginatedRows.map((row) => {
                            const stickyData = {
                              position: "sticky",
                              left: 0,
                              zIndex: 1,
                              backgroundColor: "#fff",
                            };

                            if (modo === "previsto_x_realizado") {
                              return (
                                <TableRow hover key={row.id}>
                                  <TableCell
                                    sx={{
                                      ...TD_SX,
                                      fontWeight: 700,
                                      borderRight: "2px solid #cbd5e1",
                                      ...stickyData,
                                    }}
                                  >
                                    {row.label}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ ...TD_SX, color: corEntrada }}
                                  >
                                    {formatPrice(row.entradasPrevistas, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...TD_SX,
                                      color: corEntrada,
                                      borderRight: "1px solid #e2e8f0",
                                    }}
                                  >
                                    {formatPrice(row.entradas, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ ...TD_SX, color: corSaida }}
                                  >
                                    {formatPrice(row.saidasPrevistas, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...TD_SX,
                                      color: corSaida,
                                      borderRight: "1px solid #e2e8f0",
                                    }}
                                  >
                                    {formatPrice(row.saidas, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...TD_SX,
                                      color: corSaldo(row.saldoPrevisto),
                                    }}
                                  >
                                    {formatPrice(row.saldoPrevisto, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...TD_SX,
                                      color: corSaldo(row.saldoDia),
                                    }}
                                  >
                                    {formatPrice(row.saldoDia, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...TD_SX,
                                      fontWeight: 700,
                                      borderRight: "none",
                                      color: corSaldo(row.saldoAcumulado),
                                    }}
                                  >
                                    {formatPrice(row.saldoAcumulado, false)}
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            if (modo === "previsto") {
                              return (
                                <TableRow hover key={row.id}>
                                  <TableCell
                                    sx={{
                                      ...TD_SX,
                                      fontWeight: 700,
                                      ...stickyData,
                                    }}
                                  >
                                    {row.label}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ ...TD_SX, color: corEntrada }}
                                  >
                                    {formatPrice(row.entradasPrevistas, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{ ...TD_SX, color: corSaida }}
                                  >
                                    {formatPrice(row.saidasPrevistas, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...TD_SX,
                                      color: corSaldo(row.saldoPrevisto),
                                    }}
                                  >
                                    {formatPrice(row.saldoPrevisto, false)}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      ...TD_SX,
                                      fontWeight: 700,
                                      borderRight: "none",
                                      color: corSaldo(
                                        row.saldoAcumuladoPrevisto,
                                      ),
                                    }}
                                  >
                                    {formatPrice(
                                      row.saldoAcumuladoPrevisto,
                                      false,
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            }

                            return (
                              <TableRow hover key={row.id}>
                                <TableCell
                                  sx={{
                                    ...TD_SX,
                                    fontWeight: 600,
                                    ...stickyData,
                                  }}
                                >
                                  {row.label}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ ...TD_SX, color: corEntrada }}
                                >
                                  {formatPrice(row.entradas, false)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ ...TD_SX, color: corSaida }}
                                >
                                  {formatPrice(row.saidas, false)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    ...TD_SX,
                                    color: corSaldo(row.saldoDia),
                                  }}
                                >
                                  {formatPrice(row.saldoDia, false)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    ...TD_SX,
                                    fontWeight: 700,
                                    borderRight: "none",
                                    color: corSaldo(row.saldoAcumulado),
                                  }}
                                >
                                  {formatPrice(row.saldoAcumulado, false)}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={totalColunas}
                              align="center"
                              sx={{ py: 4, color: "#94a3b8", fontSize: 14 }}
                            >
                              Nenhuma movimentação encontrada neste período.
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                rowsPerPageOptions={[7, 15, 31]}
              />
            </CardContent>
          </Card>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FluxoDeCaixa;
